// ============================================================================
// MarketRequestReview — отзыв клиента о мастере после завершённой заявки.
//
// Ограничения проверяет СЕРВЕР (internal/markettake/review.go): отзыв может
// оставить только клиент, у которого есть завершённая заявка этому мастеру,
// с подтверждённым телефоном; один отзыв на мастера. UI лишь показывает
// доступное действие и объясняет правило — обойти сервер нельзя.
//
// Данные — существующий ReviewService (без новых proto/миграций):
//   • сводка/список — review.listReviews (rating_avg/count);
//   • публикация — review.createReview (сервер возвращает published).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useI18n } from '@/lib/i18n/I18nProvider';
import { eventBusHub } from '@/lib/eventBusHub';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import type { MarketRequest } from '@/lib/market';
import { MarketRatingBadge } from './MarketRatingBadge';

const OBJECT_TYPE = 'catalog';
const OBJECT_CODE = 'mkt_masters';

interface ReviewRow {
  id: string;
  authorId?: string;
  authorName?: string;
  guestName?: string;
  rating: number;
  text: string;
  createdAt?: string;
}

function reviewAuthor(row: ReviewRow): string {
  return row.authorName || row.guestName || 'Клиент';
}

export function MarketRequestReview({ request }: { request: MarketRequest }) {
  const { t } = useI18n();
  const userId = String(useAuthStore((s) => s.user?.id || ''));
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [avg, setAvg] = useState<number | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const masterCatalogId = request.masterCatalogId || '';
  const isAuthor = !!userId && userId === request.createdBy;
  const canReview = request.status === 'done' && !!masterCatalogId && isAuthor;

  const load = useCallback(async () => {
    if (!masterCatalogId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const resp: any = await getSDK().review.listReviews({
        objectType: OBJECT_TYPE,
        objectCode: OBJECT_CODE,
        objectId: masterCatalogId,
        page: 0,
        pageSize: 20,
      });
      setRows((resp.items || []) as ReviewRow[]);
      setAvg(typeof resp.ratingAvg === 'number' ? resp.ratingAvg : null);
      setCount(typeof resp.count === 'number' ? resp.count : null);
    } catch (e) {
      logErrorToJournal(e, 'Маркетплейс: отзывы о мастере');
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [masterCatalogId]);

  useEffect(() => {
    void load();
  }, [load, reloadTick]);

  // Живое обновление: новый/опубликованный отзыв по этому мастеру.
  useEffect(() => {
    if (!masterCatalogId) return;
    return eventBusHub.subscribe((ev) => {
      if (!ev.topic.startsWith('data.review.')) return;
      const payload = (ev.payload || {}) as Record<string, unknown>;
      if (String(payload.object_id || '') === masterCatalogId) {
        setReloadTick((n) => n + 1);
      }
    });
  }, [masterCatalogId]);

  if (!masterCatalogId) return null;

  const myReview = userId
    ? rows.find((row) => String(row.authorId || '') === userId) || null
    : null;

  const submit = async () => {
    if (!text.trim()) {
      setError(t('market.review_placeholder'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      await getSDK().review.createReview({
        objectType: OBJECT_TYPE,
        objectCode: OBJECT_CODE,
        objectId: masterCatalogId,
        rating,
        text: text.trim(),
      });
      setDone(true);
      setText('');
      setReloadTick((n) => n + 1);
    } catch (e) {
      logErrorToJournal(e, 'Маркетплейс: публикация отзыва о мастере');
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{t('market.review_title')}</h2>
        <MarketRatingBadge rating={avg} count={count} compact />
      </div>

      {loading ? (
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t('market.review_loading')}
        </div>
      ) : (
        <p className="mt-1 text-[11px] leading-5 text-muted-foreground">
          {t('market.review_hint')}
        </p>
      )}

      {myReview && (
        <div className="mt-3 rounded-xl border border-emerald-500/25 bg-emerald-500/5 px-3 py-2.5">
          <div className="flex items-center gap-2 text-[11px] font-medium text-emerald-700">
            {t('market.review_yours')}
            <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
            {myReview.rating}/5
          </div>
          <div className="mt-1 whitespace-pre-wrap break-words text-xs leading-5">{myReview.text}</div>
        </div>
      )}

      {!myReview && rows.length > 0 && (
        <div className="mt-3 space-y-2">
          {rows.slice(0, 3).map((row) => (
            <div key={row.id} className="rounded-xl border bg-background/60 px-3 py-2">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <span className="font-medium text-foreground">{reviewAuthor(row)}</span>
                <span className="inline-flex items-center gap-0.5 text-amber-600">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                  {row.rating}
                </span>
              </div>
              <div className="mt-1 whitespace-pre-wrap break-words text-xs leading-5">{row.text}</div>
            </div>
          ))}
        </div>
      )}

      {!myReview && rows.length === 0 && !loading && (
        <div className="mt-3 rounded-xl border border-dashed px-3 py-3 text-xs text-muted-foreground">
          {t('market.review_list_empty')}
        </div>
      )}

      {canReview && !myReview && !done && (
        <div className="mt-3 space-y-2">
          <div className="text-[11px] font-medium text-muted-foreground">{t('market.review_rating')}</div>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setRating(value)}
                title={`${value}`}
                aria-label={`${value}`}
                disabled={busy}
                className="rounded p-0.5"
              >
                <Star
                  className={
                    value <= rating
                      ? 'h-6 w-6 fill-amber-400 text-amber-400'
                      : 'h-6 w-6 text-muted-foreground/40'
                  }
                />
              </button>
            ))}
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder={t('market.review_placeholder')}
            disabled={busy}
            className="w-full resize-y rounded-xl border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
          {error && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}
          <Button type="button" onClick={() => void submit()} disabled={busy} className="h-9 rounded-xl">
            {busy ? t('market.review_sending') : t('market.review_submit')}
          </Button>
        </div>
      )}

      {done && (
        <div className="mt-3 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700">
          {t('market.review_thanks')}
        </div>
      )}
    </section>
  );
}
